import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { RootLayout } from '@/widgets/layout'
import { ErrorBoundary } from '@/shared/ui'
import { routes } from '@/shared/config'
import {
  DashboardLkPage,
  DashboardPage,
  DashboardProfilePage,
  CatalogSubscriptionPage,
} from '@/pages/dashboard'
import { AdminRoute, ProtectedRoute, SuperAdminRoute, resolveAuthRedirect } from '@/features/auth'
import { useUserStore } from '@/entities/user'
import { AdminPage } from '@/pages/admin'
import { MagicLinkPage } from '@/pages/magic-link'
import { AcademyRedirectPage } from '@/pages/academy-redirect'
import { RegistrationPage } from '@/pages/registration'
import { ResetPasswordPage } from '@/pages/reset-password'
import { PrivacyPolicyPage, UserAgreementPage } from '@/pages/legal'
import { OnboardingPage } from '@/pages/onboarding'
import { OnboardingOldPage } from '@/pages/onboarding-old'
import { VideoViewerPage, VideosPage } from '@/pages/videos'
import {
  CatalogGalleryTab,
  CatalogLayoutsTab,
  CatalogPage,
  CatalogTokensTab,
} from '@/pages/catalog'
import { SuperAdminLogsPage, SuperAdminUsersPage } from '@/pages/super-admin'

// Корень приложения. Раньше безусловно кидал на форму регистрации — из-за этого
// уже залогиненный человек, зайдя на app.epicgrowth.io (или через lk→app),
// видел форму входа, а в каталог попадал только по прямому /catalog. Теперь
// корень смотрит на авторизацию: гостя — на вход, залогиненного — туда же, куда
// ведёт обычный вход после логина.
export function RootRedirect() {
  const { user, isAuthenticated, isLoading } = useUserStore()

  if (isLoading) {
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to={routes.registration} replace />
  }

  return (
    <Navigate
      to={resolveAuthRedirect({
        subscriptionActive: user?.subscriptionActive,
        subscriptionStatus: user?.subscriptionStatus,
      })}
      replace
    />
  )
}

export function LegacyDashboardSubscriptionRedirect() {
  const location = useLocation()

  return (
    <Navigate
      to={{
        pathname: routes.catalogSubscription,
        search: location.search,
      }}
      replace
    />
  )
}

export const router = createBrowserRouter([
  {
    path: routes.registration,
    element: <RegistrationPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.academyPricing,
    element: <AcademyRedirectPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.resetPassword,
    element: <ResetPasswordPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.magicLink,
    element: <MagicLinkPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.userAgreement,
    element: <UserAgreementPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.privacyPolicy,
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.onboarding,
    element: <OnboardingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: routes.onboardingOld,
    element: <OnboardingOldPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            // Покупка убрана (read-only ЛК): старый маршрут покупки уводим на
            // прайсинг Академии. Дерево pages/choose-plan больше не рендерится
            // и выпадает из бандла. Глубокие пути (/choose-plan/checkout и т.п.)
            // из старых писем/закладок тоже уводим туда, а не на «/».
            path: routes.choosePlan,
            element: <Navigate to={routes.academyPricing} replace />,
          },
          {
            path: `${routes.choosePlan}/*`,
            element: <Navigate to={routes.academyPricing} replace />,
          },
          {
            path: routes.dashboard,
            element: <DashboardPage />,
            children: [
              {
                index: true,
                element: <Navigate to="lk" replace />,
              },
              {
                path: 'lk',
                element: <DashboardLkPage />,
              },
              {
                path: 'subscription',
                element: <LegacyDashboardSubscriptionRedirect />,
              },
              {
                path: 'profile',
                element: <DashboardProfilePage />,
              },
            ],
          },
          {
            path: routes.videos,
            element: <VideosPage />,
          },
          {
            path: routes.catalogSubscription,
            element: <CatalogSubscriptionPage />,
          },
          {
            path: routes.videoViewer,
            element: <VideoViewerPage />,
          },
          {
            path: routes.videoCollection,
            element: <VideoViewerPage />,
          },
          {
            path: routes.videoCollectionViewer,
            element: <VideoViewerPage />,
          },
          {
            path: routes.videoViewerLegacy,
            element: <VideoViewerPage />,
          },
          {
            element: <AdminRoute />,
            children: [
              {
                path: routes.admin,
                element: <AdminPage />,
              },
            ],
          },
          {
            element: <SuperAdminRoute />,
            children: [
              {
                path: routes.superAdmin,
                element: <Navigate to={routes.superAdminLogs} replace />,
              },
              {
                path: routes.superAdminLogs,
                element: <SuperAdminLogsPage />,
              },
              {
                path: routes.superAdminUsers,
                element: <SuperAdminUsersPage />,
              },
              {
                path: routes.superAdminBubbleMigration,
                lazy: async () => {
                  const { SuperAdminBubbleMigrationPage } = await import('@/pages/super-admin')
                  return { Component: SuperAdminBubbleMigrationPage }
                },
              },
            ],
          },
        ],
      },
      {
        path: routes.catalog,
        element: <CatalogPage />,
        children: [
          {
            index: true,
            element: <CatalogGalleryTab />,
          },
          {
            path: 'tokens',
            element: <CatalogTokensTab />,
          },
          {
            path: 'layouts',
            element: <CatalogLayoutsTab />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])
